import { useEffect, useState, type ComponentProps, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { AlertIcon, CheckIcon } from './icons';

/**
 * Admin UI vocabulary.
 *
 * The shapes the reference dashboards are built from: a card, a tinted icon
 * chip, a soft status pill, a progress row. Everything is token-driven
 * (`packages/theme`), so a customer's brand and a dark theme both arrive
 * without touching these components.
 */

export function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ─── Buttons ─────────────────────────────────────────────────────────────────

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

/*
 * Primary is Deep Emerald (600) under a white label — AA, which the signature
 * emerald is not (ADR-123) — and its own ink, so dark mode's Mint button keeps
 * a readable label without a component knowing about it.
 */
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-brand-600 font-semibold text-brand-ink shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(0_0_0/0.08)] hover:bg-brand-700',
  secondary: 'border border-line-strong bg-surface text-ink shadow-card hover:border-ink-faint/50 hover:bg-surface-sunken',
  ghost: 'bg-transparent text-ink-muted hover:bg-ink/[0.05] hover:text-ink',
  danger: 'bg-danger font-semibold text-danger-ink shadow-card hover:bg-danger/90',
};

const SIZES = {
  sm: 'min-h-8 px-3 text-xs',
  md: 'min-h-10 px-4 text-sm',
} as const;

/**
 * Defaults to `type="button"`, deliberately.
 *
 * HTML's default inside a `<form>` is `submit`, so any button that merely does
 * something — open a file picker, add a row, switch a tab — submits the form
 * instead. That bug shipped: "upload image" on the product editor saved the
 * product. A button that submits now has to say so, and the three that do
 * already did.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  className,
  ...props
}: ComponentProps<'button'> & { variant?: Variant; size?: 'sm' | 'md' }) {
  return (
    // `type` is set before the spread, so an explicit one still wins.
    <button
      type="button"
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-control font-medium whitespace-nowrap transition-all active:translate-y-px disabled:pointer-events-none disabled:opacity-50',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...props}
    />
  );
}

/**
 * A link that looks like a button — "New product", "View all". A button
 * nested inside a link is two controls where one is meant, and a screen
 * reader announces both.
 */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  className,
  ...props
}: LinkProps & { variant?: Variant; size?: 'sm' | 'md' }) {
  return (
    <Link
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-control font-medium whitespace-nowrap transition-all active:translate-y-px',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...props}
    />
  );
}

/** A square icon-only control — the toggles that sit in the top bar. */
/** Same reasoning as `Button`: never an accidental submitter. */
export function IconButton({
  className,
  label,
  variant = 'outline',
  children,
  ...props
}: ComponentProps<'button'> & {
  label: string;
  /** `ghost` drops the frame — the top bar's toggles, which sit on the bar itself. */
  variant?: 'outline' | 'ghost';
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex size-10 shrink-0 items-center justify-center rounded-control text-ink-muted transition-colors hover:text-ink disabled:pointer-events-none disabled:opacity-50',
        variant === 'outline' ? 'border border-line bg-surface hover:bg-surface-sunken' : 'hover:bg-ink/[0.05]',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

// ─── Surfaces ────────────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  padded = false,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={cx(
        'rounded-card border border-line bg-surface shadow-card',
        padded && 'p-5',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A card's own heading row, with optional action on the far side. */
export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-3">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-[13px] text-ink-muted">{subtitle}</p> : null}
      </div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}

// ─── The metric card ─────────────────────────────────────────────────────────

export type AccentTone = 'mint' | 'violet' | 'amber' | 'sky' | 'rose';

const ACCENTS: Record<AccentTone, string> = {
  mint: 'bg-accent-mint text-accent-mint-ink',
  violet: 'bg-accent-violet text-accent-violet-ink',
  amber: 'bg-accent-amber text-accent-amber-ink',
  sky: 'bg-accent-sky text-accent-sky-ink',
  rose: 'bg-accent-rose text-accent-rose-ink',
};

/**
 * The tinted square that holds a card's icon.
 *
 * The tint is what makes a row of numbers scannable — an operator learns
 * "revenue is the mint one" long before they read the label.
 */
export function IconChip({
  tone = 'sky',
  children,
}: {
  tone?: AccentTone;
  children: ReactNode;
}) {
  return (
    <span
      aria-hidden
      className={cx(
        'inline-flex size-10 shrink-0 items-center justify-center rounded-[calc(var(--radius-control)+2px)] [&>svg]:size-5',
        ACCENTS[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * A headline figure.
 *
 * The value comes pre-formatted: this component never divides, rounds, or
 * localizes a number — that happens once, in the formatter (ADR-004).
 */
export function MetricCard({
  label,
  value,
  hint,
  trend,
  tone = 'sky',
  icon,
  featured = false,
}: {
  label: string;
  value: string;
  hint?: string;
  /** A pre-formatted delta; positive and negative colour themselves. */
  trend?: { text: string; direction: 'up' | 'down' | 'flat' };
  tone?: AccentTone;
  icon: ReactNode;
  /**
   * The screen's headline figure, on the menu's forest ground with the brand's
   * glow (ADR-123). One per screen — two featured cards is none.
   */
  featured?: boolean;
}) {
  if (featured) {
    return (
      <div className="nav-ground flex h-full flex-col gap-3 rounded-card p-5 text-nav-ink shadow-raised">
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-[13px] font-medium text-nav-muted">{label}</p>
          <span
            aria-hidden
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-[calc(var(--radius-control)+2px)] bg-white/[0.07] text-brand-400 ring-1 ring-white/10 [&>svg]:size-5"
          >
            {icon}
          </span>
        </div>
        <p data-numeric className="text-[1.75rem] leading-none font-bold tracking-tight">
          {value}
        </p>
        {trend || hint ? (
          <div className="mt-auto flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            {trend ? <TrendChip direction={trend.direction} text={trend.text} inverse /> : null}
            {hint ? <span className="truncate text-nav-faint">{hint}</span> : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Card className="flex h-full flex-col gap-3 p-5 transition-shadow hover:shadow-raised">
      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 truncate text-[13px] font-medium text-ink-muted">{label}</p>
        <IconChip tone={tone}>{icon}</IconChip>
      </div>
      <p data-numeric className="text-[1.75rem] leading-none font-bold tracking-tight text-ink">
        {value}
      </p>
      {trend || hint ? (
        <div className="mt-auto flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {trend ? <TrendChip direction={trend.direction} text={trend.text} /> : null}
          {hint ? <span className="truncate text-ink-faint">{hint}</span> : null}
        </div>
      ) : null}
    </Card>
  );
}

/**
 * A change from the period before: an arrow and the words, tinted — up green,
 * down red. The words carry the sign, so colour is never the only signal.
 */
export function TrendChip({
  direction,
  text,
  inverse = false,
}: {
  direction: 'up' | 'down' | 'flat';
  text: string;
  /** On the forest ground of a featured card: Mint up, a light red down. */
  inverse?: boolean;
}) {
  return (
    <span
      className={cx(
        'inline-flex max-w-full items-center gap-1 rounded-full px-1.5 py-0.5 font-medium',
        inverse
          ? cx(
              direction === 'up' && 'bg-brand-400/15 text-brand-300',
              direction === 'down' && 'bg-[oklch(0.74_0.15_25/0.18)] text-[oklch(0.82_0.11_25)]',
              direction === 'flat' && 'bg-white/[0.07] text-nav-muted',
            )
          : cx(
              direction === 'up' && 'bg-success/10 text-success',
              direction === 'down' && 'bg-danger/10 text-danger',
              direction === 'flat' && 'bg-ink/[0.05] text-ink-muted',
            ),
      )}
    >
      <svg aria-hidden viewBox="0 0 12 12" className={cx('size-3 shrink-0', direction === 'down' && 'rotate-180', direction === 'flat' && 'hidden')}>
        <path d="M6 2.5 9.5 6.5h-7z" fill="currentColor" />
      </svg>
      <span className="truncate">{text}</span>
    </span>
  );
}

// ─── Status ──────────────────────────────────────────────────────────────────

export type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/*
 * Status is its own family, never the brand (ADR-123): an emerald admin cannot
 * also say "info" in emerald. Each pill wears a dot and its word, so a status
 * is never told by colour alone.
 */
const PILLS: Record<PillTone, string> = {
  neutral: 'bg-ink/[0.05] text-ink-muted ring-ink/10',
  success: 'bg-success/10 text-success ring-success/20',
  warning: 'bg-warning/12 text-warning ring-warning/25',
  danger: 'bg-danger/10 text-danger ring-danger/20',
  info: 'bg-info/10 text-info ring-info/20',
};

/** Soft, low-contrast status — a table of these should read calm, not alarmed. */
export function Pill({ tone = 'neutral', children }: { tone?: PillTone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs leading-5 font-medium whitespace-nowrap ring-1 ring-inset',
        PILLS[tone],
      )}
    >
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current opacity-80" />
      {children}
    </span>
  );
}

/**
 * A labelled bar — the low-stock rows in the reference.
 *
 * `value` and `max` are counts, not percentages: the component works out the
 * fill so a caller cannot accidentally pass a pre-divided float.
 */
export function ProgressRow({
  label,
  value,
  max,
  caption,
  tone,
}: {
  label: string;
  value: number;
  max: number;
  caption: string;
  tone: 'danger' | 'warning' | 'success';
}) {
  // Zero is genuinely empty — a minimum sliver would read as "a little left".
  const fill = value <= 0 || max <= 0 ? 0 : Math.min(100, Math.max(3, (value / max) * 100));
  const bar = { danger: 'bg-danger', warning: 'bg-warning', success: 'bg-success' }[tone];
  const text = { danger: 'text-danger', warning: 'text-warning', success: 'text-success' }[tone];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm text-ink">{label}</span>
        <span data-numeric className={cx('shrink-0 text-xs font-medium', text)}>
          {caption}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
        className="h-2 w-full overflow-hidden rounded-full bg-ink/[0.06]"
      >
        <div className={cx('h-full rounded-full transition-all', bar)} style={{ width: `${fill}%` }} />
      </div>
    </div>
  );
}

// ─── Forms ───────────────────────────────────────────────────────────────────

/**
 * Full width unless the caller gives a width of its own. Two width classes on
 * one element resolve by stylesheet order, not by which came last, so a
 * `w-20` passed in would silently lose to the default.
 */
function widthOr(className: string | undefined, fallback: string): string | false {
  return !/(^|\s)(\w+:)*(w|size|flex)-/.test(className ?? '') && fallback;
}

/**
 * A field's frame: a line, an emerald border and soft ring on focus (the ring
 * replaces the page's focus outline, which would sit outside the rounded box).
 */
const FIELD =
  'min-h-10 rounded-control border bg-surface px-3 text-sm text-ink shadow-[0_1px_1px_rgb(0_0_0/0.02)] transition-[border-color,box-shadow] placeholder:text-ink-faint hover:border-ink-faint/60 focus:border-brand-500 focus:ring-3 focus:ring-brand-500/20 focus:outline-none disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-70';

export function Input({ className, invalid, ...props }: ComponentProps<'input'> & { invalid?: boolean }) {
  return (
    <input
      className={cx(
        FIELD,
        widthOr(className, 'w-full'),
        invalid ? 'border-danger focus:ring-danger/20' : 'border-line-strong',
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

export function Textarea({ className, invalid, ...props }: ComponentProps<'textarea'> & { invalid?: boolean }) {
  return (
    <textarea
      className={cx(
        FIELD,
        'py-2',
        widthOr(className, 'w-full'),
        invalid ? 'border-danger focus:ring-danger/20' : 'border-line-strong',
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

/**
 * An on/off switch — for a setting that takes effect as a state rather than
 * as an answer. Always labelled: a bare switch says nothing about what it
 * switches.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-brand-600' : 'bg-line-strong',
      )}
    >
      <span
        aria-hidden
        className={cx(
          'inline-block size-5 rounded-full bg-white shadow-card transition-transform',
          checked ? 'translate-x-[1.125rem] rtl:-translate-x-[1.125rem]' : 'translate-x-0.5 rtl:-translate-x-0.5',
        )}
      />
    </button>
  );
}

export function Select({ className, ...props }: ComponentProps<'select'>) {
  return (
    <select
      className={cx(FIELD, 'border-line-strong pe-8', className)}
      {...props}
    />
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-ink">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-ink-muted">{hint}</p>
      ) : null}
    </div>
  );
}

// ─── Feedback ────────────────────────────────────────────────────────────────

export function Alert({ tone = 'error', children }: { tone?: 'error' | 'success' | 'info'; children: ReactNode }) {
  const tones = {
    error: 'border-danger/25 bg-danger/[0.06] text-danger',
    success: 'border-success/25 bg-success/[0.06] text-success',
    info: 'border-info/20 bg-info/[0.06] text-ink',
  } as const;

  // An icon as well as a colour, so the kind of message is never told by colour alone.
  const Icon = tone === 'success' ? CheckIcon : AlertIcon;

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx('flex items-start gap-2.5 rounded-control border px-3.5 py-2.5 text-sm', tones[tone])}
    >
      <Icon className={cx('mt-0.5 size-4 shrink-0', tone === 'info' && 'text-info')} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    // Centred in whatever height it is given: a full-height list card with
    // nothing in it reads as an empty page, not as a page cut short.
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      {icon ? (
        // A soft halo around the chip: an empty list should look calm, not broken.
        <span className="mb-1 rounded-[1.125rem] bg-brand-500/[0.06] p-2 ring-1 ring-brand-500/10">
          <IconChip tone="mint">{icon}</IconChip>
        </span>
      ) : null}
      <p className="text-[15px] font-semibold text-ink">{title}</p>
      {body ? <p className="max-w-sm text-sm text-ink-muted">{body}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx('skeleton rounded-control', className)} />;
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="flex flex-col gap-3 p-5">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton key={column} className="h-5" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function MetricSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: count }, (_, index) => (
        <Card key={index} className="p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-32" />
            </div>
            <Skeleton className="size-10" />
          </div>
        </Card>
      ))}
    </div>
  );
}

/** Confirmation for anything destructive or irreversible (07-Admin rule 1). */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // On a phone the question rises from the bottom, where the thumb already is.
      className="animate-fade fixed inset-0 z-50 flex items-end justify-center bg-scrim/45 p-3 backdrop-blur-[3px] sm:items-center sm:p-4"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onCancel();
      }}
    >
      <div className="animate-dialog w-full max-w-md rounded-float border border-line bg-surface p-6 shadow-float">
        <div className="flex items-start gap-4">
          <span
            aria-hidden
            className={cx(
              'inline-flex size-10 shrink-0 items-center justify-center rounded-full',
              tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-brand-50 text-brand-600',
            )}
          >
            {tone === 'danger' ? <AlertIcon className="size-5" /> : <CheckIcon className="size-5" />}
          </span>
          <div className="min-w-0 pt-1">
            <h2 className="text-[17px] font-semibold text-ink">{title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{body}</p>
          </div>
        </div>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={tone} onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Small controls ──────────────────────────────────────────────────────────

/**
 * Copies a value — an address for a courier's form, an order number for a
 * message — and says so for a moment. The words come from the caller, so this
 * stays free of the translator.
 */
export function CopyButton({
  value,
  label,
  copiedLabel,
  className,
}: {
  value: string;
  label: string;
  copiedLabel: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <Button
      variant="ghost"
      size="sm"
      className={className}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true));
      }}
    >
      {copied ? copiedLabel : label}
    </Button>
  );
}

/** A row's tick box — selection on a list. Labelled for a screen reader, since it sits alone in a cell. */
export function Checkbox({
  label,
  className,
  ...props
}: Omit<ComponentProps<'input'>, 'type'> & { label: string }) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      className={cx('size-4 cursor-pointer rounded accent-brand-600', className)}
      {...props}
    />
  );
}
